import { ReactElement, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, FormControl, IconButton, MenuItem,
  Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  IAcquisitionDataSchema,
  ICondition,
  IDashboardAcquisitionEvent
} from "../types";
import { cloneObject } from "../../../system/utils";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../configuration/configuration";
import StyledTableRow from "../../../components/styledTableRow";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";

interface IProps {
  event: IDashboardAcquisitionEvent | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  schema: IAcquisitionDataSchema;
}


const ConditionsDialog = ({ event, open, setOpen, schema }: IProps): ReactElement => {
  const [ conditions, setConditions ] = useState<ICondition[]>([]);

  useEffect(() => {
    if (event) {
      setConditions(cloneObject(event.conditions));
    }
  }, [ event ]);

  const onAccept = () => {
    if (event) {
      event.conditions = conditions;
    }

    setOpen(false);
  };

  const onCancel = () => {
    setOpen(false);
  };

  const addCondition  = () => {
    const selectedEvent = event && schema.events[event.field] ? schema.events[event.field].filter : null;
    const field = selectedEvent ? Object.keys(selectedEvent)[0] : null;

    setConditions([
      ...conditions,
      {
        field: field ?? "",
        value: 0,
        comparison: "="
      } ]);
  };

  const removeCondition  = (index: number) => {
    const newArray = [ ...conditions ];
    newArray.splice(index, 1);
    setConditions(newArray);
  };

  const updateItemsCondition = (index: number, field: keyof ICondition, value: any) => {
    const newArray = [ ...conditions ];
    const item = newArray[index] as any;
    item[field] = value;
    setConditions(newArray);
  };

  return (
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={open} maxWidth="md">
      <DialogTitle id="customized-dialog-title">
        Conditions Config
      </DialogTitle>
      <DialogContent dividers={true} >
        {event &&
          <TableContainer sx={{ maxHeight: 270 }}>
            <Table stickyHeader={true} size={"small"} >
              <TableHead>
                <TableRow>
                  <TableCell className="table-header" style={{ minWidth: 180 }}>
                    <IconButton sx={{ padding: 2 }} onClick={addCondition} disabled={conditions.length > 4}>
                      <Tooltip title={"Add condition"}>
                        <Add style={{ color: getPalette().statusColor.info.background }}/>
                      </Tooltip>
                    </IconButton>
                    Field
                  </TableCell>
                  <TableCell className="table-header" style={{ minWidth: 100 }}>Comparison</TableCell>
                  <TableCell className="table-header" style={{ minWidth: 100 }}>Value</TableCell>
                  <TableCell className="table-header" style={{ minWidth: 50 }}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {conditions.map((row, index) => {
                  const filter = event ? schema.events[event.field].filter : {};
                  const selectedFilter = filter[row.field] ? filter[row.field] : null;
                  const comparisons: string[] = selectedFilter ? selectedFilter.comparisons : [];

                  const list = selectedFilter ? selectedFilter.list : null;

                  return (
                    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`series-row-${index}`}>
                      <TableCell>
                        <FormControl className="w-100">
                          <Select
                            variant="standard"
                            className="w-100"
                            value={row.field}
                            displayEmpty={true}
                            onChange={(ev: SelectChangeEvent) => updateItemsCondition(index, "field", ev.target.value)}>
                            {Object.keys(filter).map(field => <MenuItem key={`field-${index}-${field}`} value={field}>{field}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell>
                        <FormControl className="w-100">
                          <Select
                            variant="standard"
                            value={row.comparison}
                            displayEmpty={true}
                            onChange={(ev: SelectChangeEvent) => updateItemsCondition(index, "comparison", ev.target.value)}>
                            {comparisons.map(field => <MenuItem key={`comparisons-${index}-${field}`} value={field}>{field}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell>
                        <FormControl className="w-100">
                          { list
                            ? <Select
                              variant="standard"
                              className="w-100"
                              value={`${row.value}`}
                              displayEmpty={true}
                              onChange={(ev: SelectChangeEvent) => updateItemsCondition(index, "value", ev.target.value)}>
                              {list.map((field) => <MenuItem key={`option-${index}-${field}`} value={field}>{field}</MenuItem>)}
                            </Select>
                            : <TextField
                              variant="standard"
                              size="small"
                              value={row.value}
                              type="number"
                              onChange={(ev) => updateItemsCondition(index, "value", parseFloat(ev.target.value as string))}
                            />
                          }
                        </FormControl>
                      </TableCell>

                      <TableCell>
                        <IconButton sx={{ padding: 2 }} onClick={() => removeCondition(index)} >
                          <Tooltip title={"Remove condition"}>
                            <Delete style={{ color: getPalette().statusColor.error.background }}/>
                          </Tooltip>
                        </IconButton>
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConditionsDialog;
